import type { ApiInvoice } from '#/api/invoice/types/ApiInvoice';
import type { ApiAddress } from '#/api/types/ApiAddress';
import type { ApiAdmin } from '#/api/types/ApiAdmin';
import type { ApiAdminInvite } from '#/api/types/ApiAdminInvite';
import type { ApiAdminRequest } from '#/api/types/ApiAdminRequest';
import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import type { ApiListingMiniExtra } from '#/api/types/ApiListingMini';
import type { ApiPreferredListingType } from '#/api/types/ApiPreferredListingType';
import type { ApiSlideshowData } from '#/api/types/ApiSlideshowData';
import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import type { ApiOrgId } from '#/api/types/ApiTypedId';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { ApiOrgSize } from '#/modules/listing/orgs/api/types/ApiOrgSize';
import type { ApiOrgTimeActive } from '#/modules/listing/orgs/api/types/ApiOrgTimeActive';
import type { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';

import type { OrgDashboardApiInvoiceMini } from './OrgDashboardApiInvoiceMini';

export type OrgDashboardApiYesNoDontKnow = 'YES' | 'NO' | 'DONT-KNOW';

export type OrgDashboardApiOrg = {
  id: ApiOrgId;
  created: ApiISODateTimeString;
  name: string;
  type: 'ORG';
  orgType: ApiOrgType;
  description: string | null;
  descriptionTruncated: string | null;
  mission: string | null;
  missionTruncated: string | null;
  links: string[];
  email: string;
  phone: string;
  address: ApiAddress;
  locationType: 'ONSITE' | 'REMOTE' | 'HYBRID';
  url: Record<I18nLocale, string>;
  areasOfFocus: ApiAreaOfFocus[];
  areasOfFocusKeys: ApiAreaOfFocus[];
  jobs: ApiListingMiniExtra[];
  internships: ApiListingMiniExtra[];
  volops: ApiListingMiniExtra[];
  events: ApiListingMiniExtra[];
  logo: ApiFilestackImageData | null;
  banner: ApiFilestackImageData | null;
  slideshowItems: ApiSlideshowData[];
  hiring?: boolean;
  seekingVolunteers?: boolean;
  hasUpcomingEvents?: boolean;
  is501c3: boolean;
  isPublished: boolean;
  isApproved: boolean;
  isAllowedToPost: boolean;
  isFrozen: boolean;
  canPayByCheck: boolean;
  canPostVolops: boolean;
  canPostEvents: boolean;
  canBuyCredits: boolean;
  canDeleteOrg: boolean;
  canAddAdmins: boolean;
  canAddOwners: boolean;
  hasInvoices: boolean;
  hasApi: boolean;
  hasAts: boolean; // @TODO: it's always true, remove
  canPostAnonymously: boolean;
  postingRequiresApproval: boolean;
  locale: I18nLocale;
  governmentId: string | null;
  linkedinId: number | null;
  vatNumber: string | null;
  donationLink: string | null;
  inCountryThatRequiresVat: boolean;
  featureFairRegistrations: boolean;
  admins: ApiAdmin[];
  adminRequests: ApiAdminRequest[];
  invites: ApiAdminInvite[];
  invoices: OrgDashboardApiInvoiceMini[];
  frozenListings: {
    id: string;
    name: string;
    type: string;
  }[];
  itemsInCart: number;
  credits: {
    JOB: number;
    INTERNSHIP: number;
  };
  promoCredits: {
    JOB: number;
    INTERNSHIP: number;
  };
  membershipPromoCredits: number;
  searchApiKey: string;
  greenhouse: {
    boardToken: string;
    sourceToken: string;
    autoPublish: boolean;
    byUser: {
      id: string;
      name: string;
      email: string;
    } | null;
  } | null;
  jazzhr: {
    customerId: string;
    autoPublish: boolean;
    byUser: {
      id: string;
      name: string;
      email: string;
    } | null;
  } | null;
  prices: {
    listings: {
      JOB: number;
      INTERNSHIP: number;
    };
    usListings: {
      JOB: number;
      INTERNSHIP: number;
    };
    CREDIT_PACKS: Record<
      string,
      {
        type: string;
        price: number;
        quantity: number;
      }
    >;
  };
  recommendationsEnabled: boolean;
  surveyBannerEnabled: boolean;
  preferredListingTypes?: ApiPreferredListingType[];
  relatedJobAreas?: boolean | null;
  relatedJobCount?: number | null;
  clients?: string | null;
  isBCorp?: OrgDashboardApiYesNoDontKnow;
  timeActive?: ApiOrgTimeActive | null;
  services?: string | null;
  orgSize?: ApiOrgSize | null;
  orgDataHash: string;
  membership: {
    isActive: boolean;
    isAllowed: boolean;
    expires: ApiISODateTimeString | null;
    unpaidInvoice: ApiInvoice | null;
  };
  vmLegacyId: string | null;
  isIntegrationPartner: boolean;
};

export const isOrgDashboardApiOrg = (obj: unknown): obj is OrgDashboardApiOrg =>
  typeof obj === 'object' && obj !== null && 'orgDataHash' in obj;
